import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

// Cache the current user + short-lived access token in sessionStorage
// (not localStorage) so closing the last browser window auto-logs the
// user out. Backend refresh cookie is also a session cookie when
// AUTO_LOGOUT_ON_CLOSE is enabled server-side (default). Legacy
// localStorage keys are proactively cleared so old sessions don't leak
// across a page refresh after this rollout.
const USER_KEY = "miro_user";
const TOKEN_KEY = "miro_token";
try {
  if (localStorage.getItem(USER_KEY) || localStorage.getItem(TOKEN_KEY)) {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
  }
} catch { /* ignore */ }

const authStore = {
  getUser() {
    try {
      const raw = sessionStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  },
  setUser(u) {
    try { sessionStorage.setItem(USER_KEY, JSON.stringify(u)); } catch { /* ignore */ }
  },
  clearUser() {
    try { sessionStorage.removeItem(USER_KEY); } catch { /* ignore */ }
  },
  setToken(t) {
    try { sessionStorage.setItem(TOKEN_KEY, t); } catch { /* ignore */ }
  },
  clearToken() {
    try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  },
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => authStore.getUser());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Hydrate on load. Cookie-based sessions authenticate silently.
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        authStore.setUser(r.data);
      })
      .catch(() => {
        authStore.clearUser();
        authStore.clearToken();
        setUser(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      if (r.data.access_token) {
        authStore.setToken(r.data.access_token);
      } else {
        authStore.clearToken();
      }
      authStore.setUser(r.data.user);
      setUser(r.data.user);
      return r.data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (payload) => {
    setLoading(true);
    try {
      const r = await api.post("/auth/register", payload);
      authStore.setUser(r.data);
      setUser(r.data);
      return r.data;
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    const r = await api.get("/auth/me");
    setUser(r.data);
    authStore.setUser(r.data);
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Ignore — always tear down local state.
    }
    authStore.clearToken();
    authStore.clearUser();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, login, register, logout, refresh, loading }),
    [user, login, register, logout, refresh, loading],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
